import { createContext, useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import * as Sentry from '@sentry/react'
import queryString from 'query-string'
import moment from 'moment'
import store from 'store'
import { useEffectOnce } from 'react-use'

import { getSessionData, NO_TEAM } from 'utils/session'
import { resetAnalytics } from 'track'
import { ms } from 'utils/millisecond'
import { isPlainObject } from 'utils/obj'
import RBACFallback from 'rbac.json'
import agent from 'agent'

export { NO_TEAM }

const BASE_SESSION = {
  change_requested: false,
  company_alias: '',
  company_contract_expires_at: moment().add(8, 'days').toDate(),
  company_id: '',
  company_is_alfi_enabled: false,
  company_name: '',
  expires_at: '',
  first_name: '',
  header: '',
  identifier: '',
  last_login: '',
  last_name: '',
  prefs: {},
  privileges: [],
  renew_token: '',
  roles: [],
  teams: [],
  tier: 'Enterprise',
  token: ''
}

const DEFAULT_AUTH_VALUES = {
  RBAC: {},
  authHeader: '',
  companyDisplayName: '',
  companyId: '',
  companyIsAlfiEnabled: false,
  companyPrefs: {},
  companyPrivileges: [],
  contractExpirationImminent: false,
  currentTeam: {},
  currentTeamId: '',
  currentTeamRoles: [],
  daysUntilContractExpiration: 99,
  endSession: () => {},
  fetchSession: () => {},
  getTeamDetails: () => {},
  hasCompanyPrivilege: () => {},
  hasElevatedCompanyRole: false,
  hasElevatedRole: false,
  hasElevatedTeamRole: false,
  hasTeamPrivilege: () => {},
  hasUpgradeBeenRequested: false,
  isAdmin: false,
  isFreeEdition: false,
  isRbacEnabled: false,
  isSuper: false,
  privilegesForTeam: [],
  ready: false,
  renewSession: () => {},
  roles: [],
  session: BASE_SESSION,
  startSession: () => {},
  teams: '',
  tier: 'Enterprise'
}
export const AuthContext = createContext(DEFAULT_AUTH_VALUES)

async function getRbacSummary(session, teamId) {
  let rbacSummary
  try {
    // do not cache the data from this call
    // the endpoint is fast and returns static data
    // and if it changes, we need it to be updated in the browser asap
    const { data } = await agent.get(
      `/roledefinitions/summary?teamId=${teamId}`,
      {
        headers: {
          Authorization: session.header
        }
      }
    )

    rbacSummary = data

    // cache recently-retrieved data in local storage
    store.set('rbac-summary', {
      date: new Date(),
      summary: rbacSummary
    })
  } catch (e) {
    // use previously cached copy if the api call fails
    const storedSummary = store.get('rbac-summary')
    if (storedSummary) {
      const { date, summary } = storedSummary
      // Only refresh summary if older than 24 hours
      if (date && summary && new Date() - new Date(date) <= ms.days(1)) {
        return summary
      }
    } else {
      // and if we don't have it cached then use our fixture copy of the RBAC data
      rbacSummary = RBACFallback
    }
  }

  return rbacSummary
}

async function getUserSession(session) {
  let res = await agent.get('/users/sessions?getCompanySession=true', {
    headers: {
      Authorization: session.header
    }
  })
  return res.data
}

async function renewUserSession(session) {
  const res = await agent.post(
    '/users/renew/rbac',
    queryString.stringify({
      email: session.identifier,
      companyId: session.company_id,
      renewToken: session.renew_token
    }),
    {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    }
  )
  return res.data
}

export function setActiveTeamIdInLocalStorage(teamId, companyId) {
  const activeTeamIdValue = store.get('lastActiveOrgId')
  if (isPlainObject(activeTeamIdValue)) {
    return store.set('lastActiveOrgId', {
      ...activeTeamIdValue,
      [companyId]: teamId
    })
  } else {
    store.set('lastActiveOrgId', {
      [companyId]: teamId
    })
  }
}

export function getActiveTeamIdFromLocalStorage(companyId) {
  const activeTeamIdValue = store.get('lastActiveOrgId')

  if (typeof activeTeamIdValue === 'string') {
    return activeTeamIdValue
  } else if (isPlainObject(activeTeamIdValue)) {
    return activeTeamIdValue[companyId]
  }
}

export function AuthProvider({
  initialSession = BASE_SESSION,
  initialReady = false,
  initialCurrentTeamId = '',
  initialIsRbacEnabled = false,
  initialRbac = RBACFallback,
  children
}) {
  const [authState, setAllAuth] = useState(() => ({
    ready: initialReady,
    session: initialSession,
    currentTeamId: initialCurrentTeamId,
    isRbacEnabled: initialIsRbacEnabled,
    RBAC: initialRbac
  }))

  const { session, ready, currentTeamId, isRbacEnabled, RBAC } = authState

  const endSession = useCallback(async () => {
    if (session.header) {
      try {
        await agent.delete('/users/auth')
        // eslint-disable-next-line no-empty
      } catch (err) {}
    }

    // clear segment.com analytics data
    resetAnalytics()

    store.remove('session')
    store.remove('onboardingAccessed')
    // discard the choice of resetting secret key when user log out
    store.remove('confirmSecretReset')
    store.remove('pendingEnvironmentRequestID')
    setAllAuth(current => ({
      ...current,
      ready: true,
      session: BASE_SESSION,
      currentTeamId: '',
      isRbacEnabled: false
    }))
  }, [session.header, setAllAuth])

  const startSession = useCallback(
    async (activeSession = store.get('session'), activeTeamId) => {
      const hasSession = activeSession?.company_id

      if (!hasSession) {
        setAllAuth(current => ({
          ...current,
          ready: true
        }))
        return
      }
      activeTeamId =
        activeTeamId ||
        getActiveTeamIdFromLocalStorage(activeSession.company_id)

      const isExpired = new Date(activeSession.expires_at) <= new Date()
      let updatedSession = activeSession
      try {
        if (isExpired) {
          updatedSession = await renewUserSession(activeSession)
        } else {
          updatedSession = await getUserSession(activeSession)
        }
      } catch (e) {
        return endSession()
      }

      const { sessionToStart, activeTeam } = getSessionData(
        updatedSession,
        activeTeamId
      )

      if (process.env.NODE_ENV === 'production') {
        Sentry.setUser({
          email: sessionToStart.identifier,
          id: sessionToStart.identifier
        })
      }

      const rbacSummary = await getRbacSummary(sessionToStart, activeTeamId)

      setAllAuth({
        currentTeamId: activeTeam.identifier,
        isRbacEnabled: true,
        RBAC: rbacSummary,
        ready: true,
        session: sessionToStart
      })

      store.set('session', sessionToStart)
      setActiveTeamIdInLocalStorage(
        activeTeam.identifier,
        updatedSession.company_id
      )
      return sessionToStart
    },
    [endSession]
  )

  const renewSession = useCallback(async () => {
    try {
      const nextSession = await startSession(await renewUserSession(session))
      return nextSession
    } catch (err) {
      endSession()
    }
  }, [endSession, session, startSession])

  const fetchSession = useCallback(async () => {
    try {
      const nextSession = await startSession(await getUserSession(session))
      return nextSession
    } catch (e) {
      endSession()
    }
  }, [endSession, session, startSession])

  const setActiveTeamId = useCallback(
    async teamId => {
      setAllAuth(current => ({
        ...current,
        currentTeamId: teamId
      }))

      setActiveTeamIdInLocalStorage(teamId, session.company_id)
    },
    [session.company_id]
  )

  const getTeamDetails = useCallback(
    function getTeamDetails(teamId) {
      return (
        session.teams.find(t => t.identifier === teamId) || {
          header: '',
          privileges: [],
          roles: []
        }
      )
    },
    [session.teams]
  )
  const hasCompanyPrivilege = useCallback(
    privilege => Boolean(session.privileges.includes(privilege?.key)),
    [session.privileges]
  )

  const privilegesForTeam = useCallback(
    function privilegesForTeam(teamId) {
      return getTeamDetails(teamId).privileges
    },
    [getTeamDetails]
  )

  const hasTeamPrivilege = useCallback(
    (teamId, privilege) =>
      privilegesForTeam(teamId).includes(privilege?.key) ||
      // Some high level roles can manage teams even if not a member
      hasCompanyPrivilege(privilege),
    [hasCompanyPrivilege, privilegesForTeam]
  )

  const authContextValue = useMemo(() => {
    const currentTeam = getTeamDetails(currentTeamId)
    const currentRoles = session.roles.concat(currentTeam.roles)
    const daysUntilContractExpiration = Math.ceil(
      (new Date(session.company_contract_expires_at) - new Date()) /
        (1000 / 60 / 60 / 24)
    )

    return {
      RBAC,
      authHeader: session.header,
      companyDisplayName: session.company_alias || session.company_name,
      companyId: session.company_id,
      companyIsAlfiEnabled: session.company_is_alfi_enabled,
      companyPrefs: session.prefs,
      companyPrivileges: session.privileges,
      contractExpirationImminent: daysUntilContractExpiration <= 7,
      currentTeam,
      currentTeamId,
      currentTeamRoles: currentTeam.roles,
      daysUntilContractExpiration: daysUntilContractExpiration,
      endSession,
      fetchSession,
      getTeamDetails,
      hasCompanyPrivilege,
      hasElevatedCompanyRole: currentRoles
        .filter(role => role !== 'DEV' && role !== 'ADMIN')
        .some(role => role !== RBAC.roles.company.COMPANY_USER.key),
      hasElevatedRole: currentRoles.some(
        role => role.includes('MANAGER') || role.includes('ADMIN')
      ),
      hasElevatedTeamRole: currentTeam.roles.some(
        role => role !== RBAC.roles.team.TEAM_USER.key
      ),
      hasTeamPrivilege,
      hasUpgradeBeenRequested: Boolean(session.change_requested),
      isAdmin: currentRoles.some(role => role === 'COMPANY_ADMIN'),
      isFreeEdition: session.tier === 'Free',
      isRbacEnabled,
      isSuper: currentRoles.some(
        role =>
          role === 'COMPANY_ADMIN' ||
          role === 'COMPANY_MANAGER' ||
          role === 'TEAM_MANAGER'
      ),
      privilegesForTeam,
      ready,
      renewSession: renewSession,
      roles: currentRoles,
      session,
      setActiveTeamId,
      startSession: startSession,
      teams: session.teams,
      tier: session.tier
    }
  }, [
    RBAC,
    currentTeamId,
    endSession,
    fetchSession,
    getTeamDetails,
    hasCompanyPrivilege,
    hasTeamPrivilege,
    isRbacEnabled,
    privilegesForTeam,
    ready,
    renewSession,
    session,
    setActiveTeamId,
    startSession
  ])

  useEffectOnce(() => {
    startSession()
  })

  return (
    <AuthContext.Provider value={authContextValue}>
      {children}
    </AuthContext.Provider>
  )
}

AuthProvider.propTypes = {
  children: PropTypes.node,
  initialCurrentTeamId: PropTypes.string,
  initialIsRbacEnabled: PropTypes.bool,
  initialRbac: PropTypes.object,
  initialReady: PropTypes.bool,
  initialSession: PropTypes.object
}
